import { cloneDeep, isString } from 'lodash';
import toInteger from 'lodash/toInteger';
import type { Editor } from '@/editor';
import { getFramework } from '@/framework';
import {
    createCanvas,
    cutTransparentArea,
    exportSize,
    fetchVectorText,
    getImgInfoAsync,
    limitMaxImageInfo,
    loadImageAsync,
} from '@/utils/file-kit';
import { computerBgRatio, getImgLayerProps } from '@/utils/layer-kit';
import {
    ImgCategoryType,
    LayerType,
    MaterialIdsType,
    StretchOptionType,
    type IBorderRadiusProps,
    type IImageAiTextExtension,
    type IImageLayerProps,
    type IImageTransform,
    type ILayerProps,
} from '@/version-types';
import { defaultImageTransform, getDefaultImageLayerData } from '../constants/default-image-data';
import { loadAiTextCanvasFontFamily, renderAiTextCanvas } from './ai-text-canvas';

/**
 * 绘制圆角
 *
 * @param canvas - Canvas 画布元素对象
 * @param radius - 圆角
 * @param x - 水平坐标
 * @param y - 垂直坐标
 */
export const drawBorderRadius = (canvas: HTMLCanvasElement, radius: IBorderRadiusProps, x: number, y: number) => {
    const { width, height } = canvas;

    const size = width > height ? height / 2 : width / 2;
    const dealRadius = (value: number) => {
        return toInteger(value * 0.01 * size);
    };

    const ctx = canvas.getContext('2d');
    if (ctx) {
        ctx.globalCompositeOperation = 'destination-in';
        ctx.beginPath();
        // 替换 roundRect，使用 arcTo 绘制圆角矩形
        const rt = dealRadius(radius.rightTop);
        const rb = dealRadius(radius.rightBottom);
        const lb = dealRadius(radius.leftBottom);
        const lt = dealRadius(radius.leftTop);

        ctx.moveTo(0, lt);
        // 左上角
        ctx.arcTo(0, 0, lt, 0, lt);
        // 顶边
        ctx.lineTo(width - rt, 0);
        // 右上角
        ctx.arcTo(width, 0, width, rt, rt);
        // 右边
        ctx.lineTo(width, height - rb);
        // 右下角
        ctx.arcTo(width, height, width - rb, height, rb);
        // 底边
        ctx.lineTo(lb, height);
        // 左下角
        ctx.arcTo(0, height, 0, height - lb, lb);
        // 左边
        ctx.lineTo(0, lt);

        ctx.fillStyle = '#000';
        ctx.fill();
        ctx.globalCompositeOperation = 'source-over';
    }
};

/**
 * 读取蒙版宽高比
 *
 * @param url - 蒙版图片链接；空链接表示移除蒙版
 * @returns 蒙版宽高比；移除蒙版时返回 1
 */
export const getMaskRatio = async (url: string) => {
    if (!url) return 1;
    if (/\.svg$/.test(url)) {
        const svgMaskText = await fetchVectorText(url);
        const { width, height } = exportSize(svgMaskText);
        return width / height;
    }
    const img = await loadImageAsync(url);
    return img.width / img.height;
};

/**
 * 使用已解析的宽高比同步设置图层蒙版
 *
 * 供图片图层在原子更新事务内使用，避免异步读取蒙版尺寸期间覆盖其他协同字段。
 *
 * @param layer - 图层属性
 * @param url - 蒙版图片链接地址
 * @param materialId - 蒙版素材 ID
 * @param maskRatio - 已解析的蒙版宽高比
 */
export const applyResolvedMask = (layer: ILayerProps, url: string, materialId: string, maskRatio: number) => {
    const data = layer.data as IImageLayerProps;
    data.maskURL = url;

    layer.materialIds = layer.materialIds || {};
    if (url === '') {
        layer.materialIds[MaterialIdsType.MASK] = [];
        layer.shine = false;
        data.stretchOption = StretchOptionType.fill;
    } else {
        layer.materialIds[MaterialIdsType.MASK] = [materialId];
        layer.shine = true;
        data.stretchOption = StretchOptionType.cover;
        // 需要确保初次添加蒙版时，按照原蒙版大小缩放图层、图片
        const originRatio =
            (layer.width * data.imageTransform.widthRatio) / (layer.height * data.imageTransform.heightRatio);
        if (originRatio > maskRatio) {
            layer.left += Math.ceil((layer.width - layer.height * maskRatio) / 2);
            layer.width = Math.ceil(layer.height * maskRatio);
        } else {
            layer.top += Math.ceil((layer.height - layer.width / maskRatio) / 2);
            layer.height = Math.ceil(layer.width / maskRatio);
        }
        const layerRatio = layer.width / layer.height;
        let widthRatio = 1,
            heightRatio = 1;
        if (originRatio > layerRatio) {
            widthRatio = originRatio / layerRatio;
        } else {
            heightRatio = layerRatio / originRatio;
        }
        const imageTransform = {
            ...data.imageTransform,
            offsetX: 0,
            offsetY: 0,
            widthRatio,
            heightRatio,
        };
        data.imageTransform = imageTransform;
        data.minimumMask = true;
    }
};

/**
 * 设置图层蒙版
 *
 * @param layer - 图层属性
 * @param url - 蒙版图片链接地址
 * @param materialId - 蒙版素材 ID
 * @param resolvedMaskRatio - 可选；预先读取的蒙版宽高比
 */
export const setMask = async (layer: ILayerProps, url: string, materialId: string, resolvedMaskRatio?: number) => {
    const maskRatio = resolvedMaskRatio ?? (url ? await getMaskRatio(url) : 1);
    applyResolvedMask(layer, url, materialId, maskRatio);
};

/** 已解析的替换图片资源信息 */
export interface IResolvedImageReplacement {
    /** 经过尺寸限制处理后的图片地址 */
    url: string;
    /** 经过尺寸限制处理后的图片自然宽度 */
    naturalWidth: number;
    /** 经过尺寸限制处理后的图片自然高度 */
    naturalHeight: number;
}

/**
 * 异步解析替换图片所需的最终资源信息
 *
 * @param url - 图片地址
 * @param stageWidth - 当前舞台宽度
 * @param imageWidth - 可选；已知图片宽度
 * @param imageHeight - 可选；已知图片高度
 * @returns 经过图片信息读取和最大尺寸限制后的资源信息
 */
export const resolveImageReplacement = async (
    url: string,
    stageWidth: number,
    imageWidth?: number,
    imageHeight?: number,
): Promise<IResolvedImageReplacement> => {
    let newImageWidth = 0;
    let newImageHeight = 0;
    // 如果没有传入图片信息就自己获取
    if (imageWidth === undefined || imageHeight === undefined) {
        // 获取图片信息
        const { width: imageWidth, height: imageHeight } = await getImgInfoAsync(url);
        newImageWidth = imageWidth;
        newImageHeight = imageHeight;
    } else {
        newImageWidth = imageWidth;
        newImageHeight = imageHeight;
    }

    // 如果宽大于1.5倍舞台宽高 就按1.5倍舞台宽高计算
    const limitResult = limitMaxImageInfo(stageWidth, url, newImageWidth, newImageHeight);
    newImageWidth = limitResult.naturalWidth;
    newImageHeight = limitResult.naturalHeight;
    url = limitResult.url;

    return {
        url,
        naturalWidth: newImageWidth,
        naturalHeight: newImageHeight,
    };
};

/**
 * 把已解析的替换图片资源同步应用到图层副本
 *
 * 供正式提交临界区基于最新图层调用，避免图片信息加载期间覆盖协同字段。
 *
 * @param layer - 提交时的最新图片图层副本
 * @param replacement - 已解析的替换图片资源
 * @param prompt - 图片提示语
 * @param materialIds - 图片素材信息
 * @param resetSize - 是否按新图片比例重置图层尺寸
 * @returns 应用替换资源后的新图层副本
 */
export const applyResolvedImageReplacement = (
    layer: ILayerProps,
    replacement: IResolvedImageReplacement,
    prompt: string,
    materialIds: Record<MaterialIdsType, string[]>,
    resetSize = false,
) => {
    const { naturalWidth: newImageWidth, naturalHeight: newImageHeight, url } = replacement;

    let newLayer = cloneDeep(layer);

    if (newLayer.materialIds) {
        newLayer.materialIds[MaterialIdsType.FUNC_LIST] = materialIds[MaterialIdsType.FUNC_LIST] || [];
    }
    newLayer.materialIds = newLayer.materialIds || {};
    newLayer.materialIds[MaterialIdsType.IMAGE_MATERIAL] = materialIds[MaterialIdsType.IMAGE_MATERIAL] || [];
    newLayer.materialIds[MaterialIdsType.PHOTO_MATERIAL] = materialIds[MaterialIdsType.PHOTO_MATERIAL] || [];
    newLayer.materialIds[MaterialIdsType.FILTER] = materialIds[MaterialIdsType.FILTER] || [];

    // 重置尺寸和中心点
    if (resetSize) {
        const layerRatio = newLayer.width / newLayer.height;
        const imageRatio = newImageWidth / newImageHeight;

        if (layerRatio >= imageRatio) {
            const newWidth = newLayer.height * imageRatio;
            newLayer.left = newLayer.left + (newLayer.width - newWidth) / 2;
            newLayer.width = newWidth;
        } else {
            const newHeight = newLayer.width / imageRatio;
            newLayer.top = newLayer.top + (newLayer.height - newHeight) / 2;
            newLayer.height = newHeight;
        }
    }

    const { width: layerWidth, height: layerHeight } = newLayer;
    const imgInfo = newLayer?.data as IImageLayerProps;
    // let { stretchOption } = imgInfo;

    // 每次都要重置imageTransform
    imgInfo.naturalWidth = newImageWidth;
    imgInfo.naturalHeight = newImageHeight;
    imgInfo.url = url;
    imgInfo.cutoutMaskURL = '';
    let imageTransform: IImageTransform = {
        ...cloneDeep(defaultImageTransform),
        widthRatio: 1,
        heightRatio: 1,
    };

    // 如果是自由模式还要计算下宽高比
    imageTransform = Object.assign(
        imageTransform,
        computerBgRatio({
            imgWidth: newImageWidth,
            imgHeight: newImageHeight,
            stageHeight: layerHeight,
            stageWidth: layerWidth,
        }),
    );
    imgInfo.imageTransform = imageTransform;

    newLayer = {
        ...newLayer,
        prompt,
        data: {
            ...imgInfo,
        },
    };
    // 海外版替换逻辑处理 用来是否已替换原有主图
    if (window.isIntl && layer.category === ImgCategoryType.mainImg) {
        newLayer.extraInfo = {
            isReplaceMainImage: true,
        };
    }
    return newLayer;
};

/**
 * 异步解析资源并生成替换后的图片图层
 *
 * @param layer - 图片图层
 * @param url - 图片地址
 * @param stageWidth - 当前舞台宽度
 * @param prompt - 图片提示语
 * @param materialIds - 图片素材信息
 * @param imageWidth - 可选；已知图片宽度
 * @param imageHeight - 可选；已知图片高度
 * @param resetSize - 是否按新图片比例重置图层尺寸
 * @returns 替换后的图片图层副本
 */
export const replaceImage = async (
    layer: ILayerProps,
    url: string,
    stageWidth: number,
    prompt: string,
    materialIds: Record<MaterialIdsType, string[]>,
    imageWidth?: number,
    imageHeight?: number,
    resetSize = false,
) => {
    const replacement = await resolveImageReplacement(url, stageWidth, imageWidth, imageHeight);
    return applyResolvedImageReplacement(layer, replacement, prompt, materialIds, resetSize);
};

/**
 * 创建花字 Canvas 预览
 *
 * @param aiText - 花字内容
 * @param editor - 编辑器实例对象
 * @param color - 可选；文字颜色
 * @param background - 可选；背景颜色
 * @param rowTexts - 可选；每行或每列文字数；默认值为 `6`
 */
export const createAiTextPreview = async (
    aiText: IImageAiTextExtension,
    editor: Editor,
    color?: string,
    background?: string,
    rowTexts = 6,
) => {
    const fontFamily = await loadAiTextCanvasFontFamily(
        aiText.fontFamily,
        aiText.text,
        (originalFontFamily, text, options) => editor.services.font.loadSubset(originalFontFamily, text, options),
    );
    const canvas = renderAiTextCanvas({
        text: aiText.text,
        fontFamily,
        bold: aiText.bold,
        italic: aiText.italic,
        writingMode: aiText.writingMode,
        color,
        background,
        rowTexts,
    });
    return {
        canvas,
        width: canvas.width,
        height: canvas.height,
    };
};

/**
 * 创建花字 Canvas 蒙版
 *
 * @param aiText - AI 花字
 * @param fonts - 字体
 * @param editor - 编辑器实例对象
 * @param color - 可选；颜色
 * @param background - 可选；背景
 * @param rowTexts - 可选；文本行数；默认值为 `6`
 */
export const createAiTextCanvasMask = async (
    aiText: IImageAiTextExtension,
    _fonts: unknown,
    editor: Editor,
    color?: string,
    background?: string,
    rowTexts = 6,
) => {
    const originalFontFamily = aiText.fontFamily;
    const fontFamily = await loadAiTextCanvasFontFamily(originalFontFamily, aiText.text, (fontFamily, text, options) =>
        editor.services.font.loadSubset(fontFamily, text, options),
    );
    return renderAiTextCanvas({
        text: aiText.text,
        fontFamily,
        bold: aiText.bold,
        italic: aiText.italic,
        writingMode: aiText.writingMode,
        color,
        background,
        rowTexts,
        padding: 15,
    });
};

/**
 * 创建 AI 花字结果画布
 *
 * @param mask - 蒙版元素对象
 * @param imgUrl - 结果图链接地址
 * @param feathering - 可选；是否羽化；默认值为 `false`
 */
export const createAiTextCanvas = async (mask: HTMLCanvasElement | string, imgUrl: string, feathering = false) => {
    const resImg = await loadImageAsync(imgUrl, false);
    if (isString(mask)) {
        const maskImg = await loadImageAsync(mask, false);
        mask = createCanvas(maskImg.width, maskImg.height, maskImg).canvas;
    }
    const { canvas: maskCanvas, ctx } = createCanvas(mask.width, mask.height, mask);
    if (!feathering) {
        const { canvas: resCanvas } = createCanvas(resImg.width, resImg.height, resImg);
        ctx.globalCompositeOperation = 'source-in';
        ctx.drawImage(resCanvas, 0, 0);
        return cutTransparentArea(maskCanvas);
    } else {
        // // TODO: 抠图相关
        // const mattingResult = await getImageMattingResult({
        //     width: resImg.width,
        //     height: resImg.height,
        //     image: resImg,
        //     maskImage: maskCanvas,
        //     useBlurFilterRun: true,
        // });
        // if (mattingResult) {
        //     return cutTransparentArea(createCanvas(resImg.width, resImg.height, mattingResult).canvas);
        // }
        return null;
    }
};

export const getFlipStyle = (flipH: boolean, flipV: boolean) => {
    const flipStyle: React.CSSProperties = {
        pointerEvents: 'none',
    };

    if (flipH) {
        flipStyle.transform = 'scaleX(-1) ';
    }
    if (flipV) {
        const ry180 = 'scaleY(-1)';
        flipStyle.transform = flipStyle.transform ? flipStyle.transform + ry180 : ry180;
    }

    flipStyle.width = '100%';
    flipStyle.height = '100%';
    return flipStyle;
};

export const getInnerStyle = (
    originImgWidth: number,
    originImgHeight: number,
    offsetX: number,
    offsetY: number,
    rotate: number,
    scale: number,
    maskURL: string,
    layerWidth: number,
    layerHeight: number,
) => {
    const style = {
        width: `${originImgWidth}px`,
        height: `${originImgHeight}px`,
        transform: `translate(${offsetX + (layerWidth - originImgWidth) / 2}px, ${offsetY + (layerHeight - originImgHeight) / 2}px) rotate(${rotate}deg) scale(${+scale.toFixed(5)})`,
        transformOrigin: 'center center',
        ...(maskURL && { WebkitMaskClip: 'content-box' }),
    };

    return style;
};

export const getImageTransformInfo = (layer: ILayerProps) => {
    const { width, height } = layer;
    const {
        imageTransform: { widthRatio, heightRatio, offsetX: offsetXRatio, offsetY: offsetYRatio, scale },
    } = layer.data as IImageLayerProps;

    const originImgWidth = width * widthRatio;
    const originImgHeight = height * heightRatio;
    const offsetX = offsetXRatio * originImgWidth * scale;
    const offsetY = offsetYRatio * originImgHeight * scale;

    return { offsetX, offsetY, originImgWidth, originImgHeight };
};

export const getImageStyle = (originImgWidth: number, originImgHeight: number) => {
    return {
        width: `${originImgWidth}px`,
        height: `${originImgHeight}px`,
        pointerEvents: 'none',
        display: 'block',
    } as React.CSSProperties;
};

export const getImageMaskStyle = (maskURL: string, minimumMask: boolean | undefined) => {
    const imageWrapStyle = {} as React.CSSProperties;

    imageWrapStyle.WebkitMaskImage = `url(${maskURL})`;
    imageWrapStyle.WebkitMaskSize = minimumMask ? '100% 100%' : 'contain';
    imageWrapStyle.WebkitMaskRepeat = 'no-repeat';
    imageWrapStyle.WebkitMaskPosition = 'center';
    imageWrapStyle.transform = 'scale(1)';
    imageWrapStyle.overflow = 'hidden';

    return imageWrapStyle;
};

/**
 * 执行图片特效
 *
 * @param identity - 图层标识
 * @param layerId - 图层 ID
 * @returns 是否执行成功
 */
export const doImageEffect = async (identity: string, layerId: string) => {
    try {
        const framework = getFramework(identity);
        const layer = framework.service.flatLayer.getLayer(layerId, true);
        if (!layer || layer.layerType !== LayerType.IMAGE_LAYER) return false;
        await framework.service.edgelessCanvas.getByLayerId(layerId)?.update(false);
        return true;
    } catch (error) {
        console.error(error);
        return false;
    }
    return false;
};

export const createImageLayer = (
    url: string,
    width: number,
    height: number,
    options?: {
        left?: number;
        top?: number;
        materialId?: string;
        naturalWidth?: number;
        naturalHeight?: number;
        isPhoto?: boolean;
    },
) => {
    const { left, top, materialId, naturalWidth, naturalHeight, isPhoto } = options || {};
    // 获取图片图层默认数据
    const imageLayerData = getDefaultImageLayerData(
        url,
        width,
        height,
        left ?? 0,
        top ?? 0,
        materialId,
        naturalWidth,
        naturalHeight,
        isPhoto,
    );

    // 获取图片图层数据
    const imageLayer = getImgLayerProps(imageLayerData);

    return imageLayer;
};
